import { useEffect, useState } from "react";
import { LoadingButton } from "@mui/lab";
import {
    Autocomplete,
    Avatar,
    Box,
    Checkbox,
    CircularProgress,
    Divider,
    IconButton,
    Paper,
    Snackbar,
    TextField,
    Tooltip,
    Typography,
    Stack,
} from "@mui/material"
import { Form, FormikProvider, useFormik } from "formik";
import { toast } from "react-toastify";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";

import { useGenericQuery } from "../../../services/react-query/generic.query";
import { SystemUserTypes, UserTypes } from "../../../types";
import { CreateTeamParams, Team } from "../../../types/teams.types";
import { createTeamParams } from "../../../schemas/team.schema";
import { handleApiError } from "../../../services/react-query/react-query.client";
import { DeleteForever } from "@mui/icons-material";
import NoData from "../../no-data";
import TagsSelection from "../team-form/tags-selection";
import { Tag } from "../../../types/tags.types";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { store } from "../../../store";
import { GooglePlacesAutocomplete } from "../../address-autocomplete";
import { TeamService } from "../../../services/team.service";
import { GroupService } from "../../../services/groups.service";
import { UserSelectConfirmationDialog } from "./user-selection-confirmation-dialog";
import { ScheduleService } from "../../../services/schedule.service";
import ScheduleModal from "./schedule-modal";
import EmergencyOptions from "./team-groups-settings";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { permissionCheck } from "../../../helpers/permissionCheck";
import { customerPermissionFlags } from "../../../constants/systemPermissions.constants";
import { useUserSearch } from "../../../hooks/useUserSearch";

const styles = {
    selectContainer: {
        marginTop: '16px',
        borderRadius: 1,
        border: '0.3px solid',
        overflow: 'auto',
        p: 1,
    },
    selectItemName: {
        fontSize: '16px',
        color: '#919EAB',
        fontWeight: '400',
        fontFamily: 'Poppins, sans-serif',
        ml: 1.5,
    },
    selectItemContainer: {
        p: 1,
        ':hover': {
            background: '#F5F6F8',
            cursor: 'pointer',
        },
        maxHeight: '60px',
        height: '60px',
    }
}

interface Props {
    isEdit: boolean,
    defaultValues?: CreateTeamParams & { id: string } | null,
}

export const TeamForm: React.FC<Props> = ({ isEdit, defaultValues }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const source = searchParams.get('source');
    const groupId = searchParams.get('groupId');
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [leadersErrored, setLeadersErrored] = useState<boolean>(false);
    const [membersErrored, setMembersErrored] = useState<boolean>(false);
    const [viewSchedule, setViewSchedule] = useState<boolean>(false);
    // Removed isNewlyCreated state - no longer needed since we don't auto-fetch schedules
    const {
        selectedGroup,
    } = useSelector((state: RootState) => state.groups);

    const {
        selectedSchedule,
    } = useSelector((state: RootState) => state.schedule);

    const {
        items: teams,
        selectedTeam,
        loading: teamLoading
    } = useSelector((state: RootState) => state.teams);

    const {
        items: availableTags,
    } = useSelector((state: RootState) => state.tags);
    const {
        data: teamLeaders,
        isLoading: isTeamLeadersLoading,
    } = useGenericQuery<SystemUserTypes.Model[]>(
        'fetchTeamLeaders',
        'customerWebAppUser/getTeamLeaders',
        undefined,
        undefined,
        false,
    );

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    // Note: We no longer fetch all users upfront - the MemberSelector now uses
    // paginated search via useUserSearch hook for better performance
    
    const [selectedMembers, setSelectedMembers] = useState<UserTypes.Model[]>([]);
    const [selectedTeamLeaders, setSelectedTeamLeaders] = useState<SystemUserTypes.Model[]>([]);
    const [selectedTags, setSelectedTags] = useState<Tag[]>([]);


    const [phoneErrors, setPhoneErrors] = useState<string[]>([]);
    const [nameErrors, setNameErrors] = useState<string[]>([]);
    const isValidPhoneNumber = (phone: string) => /^\+1\d{10}$/.test(phone);

    // Helper function to determine navigation after team creation
    const getNavigationPath = (newTeam?: Team) => {
        if (source === 'group' && groupId) {
            // Navigate back to the group edit page
            return `/groups/${groupId}/edit`;
        } else if (source === 'list') {
            // Navigate back to the groups and teams list page
            return '/groups-and-teams';
        } else {
            // Default fallback - navigate to team edit page
            return newTeam ? `/teams/${newTeam.id}/edit` : '/groups-and-teams';
        }
    };

   
    const formik = useFormik<CreateTeamParams>({
        initialValues: {
            name: defaultValues?.name ?? '',
            location: defaultValues?.location ?? '',
            membersIds: defaultValues?.membersIds ?? [],
            leadersIds: defaultValues?.leadersIds ?? [],
            tags: defaultValues?.tags ?? [],
            groupId: selectedGroup?.id!,
            settings: defaultValues?.settings ?? {
                checkOnMeEnabled: false,
                defaultContacts: [{ full_name: '', phone_number: '+1' }, { full_name: '', phone_number: '+1' }],
                calenderEnabled: false
            }
        } as CreateTeamParams,
        enableReinitialize: true,
        validationSchema: createTeamParams,
        onSubmit: async (values) => {
            if (!selectedMembers) {
                setSubmitError('Please select at least one member to create a team.');
                setMembersErrored(true);
                return;
            }

            if (!selectedTeamLeaders) {
                setSubmitError('Please select at least one team leader to create a team.');
                setLeadersErrored(true);
                return;
            }

            if ((values.settings?.defaultContact ?? false)) {
                if(values.settings?.contacts) {
                    const contacts = values.settings?.contacts
                    for (let i = 0; i < contacts.length; i++) {
                        const contact = contacts[i];
                        console.log(contact)
                        if (contact && i === 0) {
                            const name = contact['full_name']
                            const nameIsValid = name !== undefined && name !== ""
                            // validate full name
                            let updatedErrors = [...nameErrors];
                            updatedErrors[i] = nameIsValid ? "" : "Must provide a contact name";
                            setNameErrors(updatedErrors);
                            if(!nameIsValid) {
                                setSubmitError(`Please provide a name for contact #${i + 1}`);
                                return
                            }
                            // validate phone number
                            const phoneNumber = contact['phone_number']
                            const phoneNumberIsValid = isValidPhoneNumber(phoneNumber)
                            updatedErrors = [...phoneErrors];
                            updatedErrors[i] = phoneNumberIsValid
                                ? ""
                                : "Must be in format +1XXXXXXXXXX (10 digits)";
                            setPhoneErrors(updatedErrors);
                            if (!phoneNumberIsValid) {
                                setSubmitError(`Contact #${i + 1} phone number must be in format +1XXXXXXXXXX (10 digits)`);
                                return
                            }
                        } else if (contact) {
                            const name = contact['full_name']
                            const nameIsValid = name !== undefined && name !== ""
                            const phoneNumber = contact['phone_number']
                            const startedToFillOutForm = nameIsValid || (phoneNumber !== "" && phoneNumber !== undefined && phoneNumber !== '+1')

                            if (startedToFillOutForm) {
                                // validate full name
                                let updatedErrors = [...nameErrors];
                                updatedErrors[i] = nameIsValid ? "" : "Must provide a contact name";
                                setNameErrors(updatedErrors);
                                if(!nameIsValid) {
                                    setSubmitError(`Please provide a name for contact #${i + 1}`);
                                    return
                                }

                                // validate phone number
                                const phoneNumberIsValid = isValidPhoneNumber(phoneNumber)
                                updatedErrors = [...phoneErrors]
                                updatedErrors[i] = phoneNumberIsValid
                                    ? ""
                                    : "Must be in format +1XXXXXXXXXX (10 digits)"
                                setPhoneErrors(updatedErrors)
                                if (!phoneNumberIsValid) {
                                    setSubmitError(`Contact #${i + 1} phone number must be in format +1XXXXXXXXXX (10 digits)`)
                                    return
                                }
                            }
                        }
                    }
                }
            }
            if (!isEdit) {
                TeamService
                .createTeam({
                    name: values.name!,
                    location: values.location,
                    membersIds: selectedMembers?.map(m => m.id),
                    leadersIds: selectedTeamLeaders?.map(t => t.id)!,
                    tags: selectedTags.map(t => t._id!),
                    groupId: selectedGroup?.id!,
                    settings: values.settings
                }, (isValid, _, newTeam) => {
                    if (!isValid) {
                        const msg = handleApiError('Error creating team');
                        toast(msg, {
                            autoClose: 1500,
                            type: 'error',
                        });
                    } else {
                        toast('Team created successfully', {
                            autoClose: 1500,
                            type: 'success',
                        });
                        
                        // Team created successfully - no need to fetch schedule automatically
                        
                        // Create schedule if calendar is enabled, but always navigate immediately
                        if(formik.values.settings?.calendarEnabled ?? false) {
                            ScheduleService.createSchedule(newTeam!.id!, (isValid) => {
                                // Schedule creation result doesn't affect navigation
                                console.log('Schedule creation result:', isValid);
                            });
                        }
                        
                        formik.resetForm();
                        // Refresh groups data if navigating back to group page
                        if (source === 'group' && groupId) {
                            GroupService.fetchAll(() => {});
                        }
                        // Small delay to ensure state updates complete before navigation
                        setTimeout(() => {
                            navigate(getNavigationPath(newTeam));
                        }, 100);
                    }
                })
            } else {
                TeamService.updateTeam(selectedTeam!, {
                    name: values.name!,
                    location: values.location,
                    membersIds: selectedMembers?.map(m => m.id),
                    leadersIds: selectedTeamLeaders?.map(t => t.id)!,
                    tags: selectedTags.map(t => t._id!),
                    groupId: selectedGroup?.id ?? selectedTeam?.groupId!,
                    settings: values.settings,
                }, (isValid) => {
                    if (!isValid) {
                        const msg = handleApiError('Error updating team');
                        toast(msg, {
                            autoClose: 1500,
                            type: 'error',
                        });
                    } else {
                        toast('Team updated successfully', {
                            autoClose: 1500,
                            type: 'success',
                        });

                        if ((formik.values.settings?.calendarEnabled ?? false) && selectedSchedule == null) {
                            console.log('createSchedule')
                            ScheduleService.createSchedule(selectedTeam!.id!, (isValid) => {
                                if (isValid) {
                                    setViewSchedule(true)
                                } else {
                                    formik.resetForm();
                                    navigate(-1);
                                }
                            })
                        } else {
                            formik.resetForm();
                            navigate(-1);
                        }
                    }
                });

            }
        }
    });

    const handleContactChange = (
        index: number,
        field: "full_name" | "phone_number",
        valueStr: string
    ) => {
        let updatedContacts = [...(formik.values.settings?.contacts ?? [])];
        updatedContacts =  structuredClone(updatedContacts)

        if (field === "phone_number") {
            if (!valueStr.startsWith("+1")) {
                valueStr = "+1";
            } else {
                valueStr = valueStr.replace(/[^\d]/g, "");
                valueStr = "+1" + valueStr.substring(1, 11);
            }
        }

        updatedContacts[index][field] = valueStr;

        // update Formik settings value
        formik.setFieldValue("settings", {
            ...formik.values.settings,
            contacts: updatedContacts,
        });

        // validate full name
        if (field === "full_name") {
            const updatedErrors = [...nameErrors];
            updatedErrors[index] = valueStr ? "" : "Must provide a contact name";
            setNameErrors(updatedErrors);
        }

        // validate phone number
        if (field === "phone_number" && valueStr.length === 12) {
            const updatedErrors = [...phoneErrors];
            updatedErrors[index] = isValidPhoneNumber(valueStr)
                ? ""
                : "Must be in format +1XXXXXXXXXX (10 digits)";
            setPhoneErrors(updatedErrors);
        }
    };

    const location = useLocation();
    useEffect(() => {
        return () => {
            // Only reset if we're actually leaving the teams area completely
            // Don't reset when navigating between team routes or back to group/list pages
            const isLeavingTeamsArea = !location.pathname.startsWith('/teams/') && 
                                     !location.pathname.startsWith('/groups/') && 
                                     location.pathname !== '/groups-and-teams';
            
            if (isLeavingTeamsArea) {
                formik.resetForm();
                TeamService.setTeam(null)
                ScheduleService.setSchedule(null)
            }
        };
    }, [location.pathname]);

    const { handleSubmit, isSubmitting, errors, touched, getFieldProps, setFieldValue } = formik;

    const selectTeamLeader = (teamLeader: SystemUserTypes.Model) => {
        if (teamLeaderIsSelected(teamLeader.id)) {
            setSelectedTeamLeaders(selectedTeamLeaders.filter((t) => t.id !== teamLeader.id));
        } else {
            setSelectedTeamLeaders([...selectedTeamLeaders, teamLeader]);
        }
    }

    const teamLeaderIsSelected = (leaderId: string) => {
        return selectedTeamLeaders.filter(t => t.id === leaderId).length > 0;
    }

    const selectMember = (member: UserTypes.Model) => {
        if (memberIsSelected(member.id!)) {
            setSelectedMembers(selectedMembers.filter((m) => m.id !== member.id));
        } else {
            setSelectedMembers([...selectedMembers, member]);
        }
    }

    const memberIsSelected = (memberId: string) => {
        return selectedMembers.filter(m => m.id === memberId).length > 0;
    }

    const handleTagSelectionChange = (tags: Tag[]) => {
        setSelectedTags(tags)
    };


    // Populate form immediately with existing team data (don't wait for member API)
    useEffect(() => {
        if (isEdit && selectedTeam != null) {
            console.log('Team form useEffect - teamId:', selectedTeam.id);
            
            // Edit mode: populate form with existing team data
            // Fetch schedule only if calendar is enabled to avoid 500 errors
            if (selectedTeam.settings?.calendarEnabled) {
                ScheduleService.getScheduleById(selectedTeam.id, (isValid) => {
                    console.log('Schedule fetch result for team:', selectedTeam.id, isValid);
                });
            }
            setFieldValue('name', selectedTeam.name)
            setFieldValue('location', null)
            setFieldValue('location', selectedTeam.location)
            setFieldValue('settings', selectedTeam.settings)
            
            // Use team.users (already loaded from groups/teams endpoint) for instant display
            // This avoids waiting for any API call - data is already in the store
            if (selectedTeam.users && selectedTeam.users.length > 0) {
                setSelectedMembers(selectedTeam.users);
            }
            
            // Set tags for edit mode
            const teamTags = availableTags.filter((t: Tag) => (selectedTeam?.tags ?? []).includes(t._id!));
            setSelectedTags(teamTags);
        } else if (!isEdit) {
            // Create mode: clear all form state
            setSelectedMembers([]);
            setSelectedTeamLeaders([]);
            setSelectedTags([]);
            setSubmitError(null);
            setLeadersErrored(false);
            setMembersErrored(false);
        }
    }, [isEdit, selectedTeam, availableTags, setFieldValue]);

    // Populate team leaders when they load (separate effect to not block form display)
    useEffect(() => {
        if (isEdit && selectedTeam != null && teamLeaders) {
            const selectedLeaders = teamLeaders.filter((leader) => selectedTeam.leadersIds?.includes(leader.id));
            setSelectedTeamLeaders(selectedLeaders);
        }
    }, [isEdit, selectedTeam, teamLeaders]);

    const submitErrors: string[] = [];

    if (formik.values.name === '') {
        submitErrors.push("Team name is required.");
    }

    // if (formik.values.location === '') {
    //     submitErrors.push("Location is required.");
    // }

    if (selectedTeamLeaders?.length === 0) {
        submitErrors.push("At least one team leader must be selected.");
    }

    if (selectedMembers?.length === 0) {
        submitErrors.push("At least one member must be selected.");
    }

    const submitDisabled =
        submitErrors.length > 0;

    return (
        <>
            <Box sx={{
                p:  2,
                spacing: 3
            }} >
                <FormikProvider value={formik}>
                    <Form noValidate onSubmit={handleSubmit}>

                        <Box
                            display='flex'
                            flexDirection='row'
                            width="100%"
                        >
                            <Box
                                display='flex'
                                flexDirection='column'
                                width='50vw'
                            >
                                <Typography fontSize={16} variant="body1" mt={2} fontWeight={500} color={'text.default'}>
                                    Team Name <Typography color="error" component="span">*</Typography>
                                </Typography>
                                <Box
                                    mb={3}
                                >
                                    <Box 
                                        width='50%'
                                    >
                                        <TextField
                                            sx={{ marginTop: '16px' }}
                                            fullWidth
                                            label="Team Name"
                                            error={Boolean(errors.name) && touched.name}
                                            helperText={errors.name}
                                            {...getFieldProps('name')}
                                        />
                                    </Box>
                                </Box>


                            
                                {/* Location Field */}
                                
                                <Typography fontSize={16} variant="body1" mt={1} fontWeight={500} color={'text.default'}>
                                    Location
                                </Typography>
                                <Box 
                                    mb={3}
                                >
                                    <Box
                                        sx={{
                                            marginTop: 2
                                        }}>
                                        <GooglePlacesAutocomplete
                                            key={`location-${formik.values.location || 'empty'}`}
                                            initialValue={formik.values.location ? {
                                                description: formik.values.location,
                                                place_id: ""
                                            } : undefined}
                                            onAddressSelect={(description: string, placeId: string) => {
                                                setFieldValue('location', description);
                                            }}
                                        />
                                    </Box>
                                </Box>
                                

                                <TagsSelection onSelectedTagsChange={handleTagSelectionChange} selectedTags={selectedTags}  />

                                {/* TEAM Leaders */}
                                <Typography fontSize={16} variant="body1" mt={4} fontWeight={500} color={'text.default'}>
                                    Team Leaders ({selectedTeamLeaders?.length} selected) <Typography color="error" component="span">*</Typography>
                                </Typography>

                                {/* TEAM Leaders */}
                                <Box mb={3}>
                                    <Box
                                        sx={{
                                            // width: width,
                                            marginTop: 1
                                        }}>
                                        <Autocomplete
                                            disablePortal
                                            sx={{ width: '100%' }}
                                            loading={isTeamLeadersLoading}
                                            renderInput={(params) =>
                                                <TextField
                                                    {...params}
                                                    sx={{ marginTop: '16px' }}
                                                    fullWidth
                                                    label="Search for team leaders"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {isTeamLeadersLoading && <CircularProgress size={16} />}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            }
                                            getOptionLabel={(option) => option?.fullname || option?.email || option?.phone}
                                            onChange={(e, value) => {
                                                return undefined
                                            }}
                                            options={teamLeaders || []}
                                            renderOption={(_, option) => !teamLeaderIsSelected(option?.id) && (
                                                <Box
                                                    key={option?.id}
                                                    display="flex"
                                                    flexDirection="row"
                                                    alignItems="center"
                                                    justifyContent="flex-start"
                                                    sx={{
                                                        ...styles.selectItemContainer,
                                                        borderRadius: 1,
                                                        background: "white",
                                                        cursor: "pointer",
                                                        padding: "8px",
                                                    }}
                                                    onClick={() => {
                                                        selectTeamLeader(option);
                                                    }}
                                                >
                                                    <Avatar
                                                        sx={{
                                                            width: 28,
                                                            height: 28,
                                                            fontSize: 14,
                                                            bgcolor: '#e3f2fd',
                                                            color: '#1976d2',
                                                            mr: 2,
                                                        }}
                                                    >
                                                        {option.fullname?.[0] || ''}
                                                    </Avatar>
                                                    <Typography sx={styles.selectItemName}>{option?.fullname}</Typography>
                                                </Box>

                                            )}
                                        />
                                    </Box>
                                </Box>

                                {/* TEAM Leaders */}
                                <Box
                                    mb={3}
                                    display="flex"
                                    flexWrap="wrap"
                                    alignItems="center"
                                    alignContent="flex-start"
                                    justifyContent="flex-start"
                                    sx={{
                                        ...styles.selectContainer,
                                        minHeight: '120px',
                                        maxHeight: 'none',
                                        borderColor: leadersErrored ? 'red' : '#ccc',
                                        overflow: 'visible',
                                        gap: 1,
                                        padding: '8px',
                                    }}
                                >
                                    {selectedTeamLeaders?.length === 0 && (
                                        <Box
                                            display="flex"
                                            justifyContent="center"
                                            alignItems="center"
                                            textAlign="center"
                                            width="100%"
                                        >
                                            <NoData
                                                title="No selected leaders"
                                                description="Select team leaders to add to the team"
                                                buttonText={null}
                                                onClick={() => { }}
                                            />
                                        </Box>
                                    )}

                                    {selectedTeamLeaders?.map((teamLeader) => (
                                        <Box
                                            key={teamLeader?.id}
                                            display="flex"
                                            flexDirection="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            sx={{
                                                ...styles.selectItemContainer,
                                                borderRadius: 2,
                                                backgroundColor: 'white',
                                                padding: '8px 12px',
                                                boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.1)',
                                                width: 'auto',
                                                minWidth: '180px',
                                                maxWidth: 'auto',
                                                flexShrink: 0,
                                            }}
                                        >
                                            {/* Avatar + Name Container */}
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Avatar
                                                    sx={{
                                                        width: 28,
                                                        height: 28,
                                                        fontSize: 14,
                                                        bgcolor: '#e3f2fd',
                                                        color: '#1976d2',
                                                        mr: 2,
                                                    }}
                                                >
                                                    {teamLeader.fullname?.[0] || ''}
                                                </Avatar>
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                        fontWeight: 500,
                                                        color: '#424242',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {teamLeader?.fullname}
                                                </Typography>
                                            </Box>
                                            <Tooltip title="Remove team leader">
                                                <IconButton
                                                    sx={{
                                                        color: '#757575',
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                            color: '#d32f2f',
                                                        },
                                                    }}
                                                    onClick={() => {
                                                        setSelectedTeamLeaders(selectedTeamLeaders.filter((t) => t.id !== teamLeader.id));
                                                    }}
                                                >
                                                    <DeleteForever />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    ))}
                                </Box>


                                <Typography
                                    fontSize={16}
                                    variant="body1"
                                    fontWeight={500}
                                    textAlign='center'
                                    mt={4}
                                    mb={2}
                                    color={'text.default'}
                                >
                                    Add Team Members ({selectedMembers?.length} selected) <Typography color="error" component="span">*</Typography>
                                </Typography>
                                
                                <MemberSelector
                                    onSelect={selectMember}
                                    teams={teams}
                                    selectedMembers={selectedMembers}
                                    setSelectedMembers={setSelectedMembers}
                                />

                            </Box>
                            <Divider
                                orientation="vertical"
                                flexItem
                                sx={{
                                    mx: 2,
                                    borderWidth: "2px",
                                    borderColor: "rgba(0, 0, 0, 0.2)",
                                }}
                            />
                            <Box
                                display='flex'
                                flexDirection='column'
                                width='30vw'
                                justifyContent="space-between"
                            >
                                {/* Team Protocols */}
                                <Box>
                                    <Typography
                                        fontSize={16}
                                        variant="h4"
                                        fontWeight={500}
                                        color={'text.default'}
                                        sx={{
                                            mb: 2,
                                        }}
                                    >
                                        Team Protocols
                                    </Typography>
                                    {/* Check On Me */}
                                    <Paper
                                        elevation={1}
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            backgroundColor: "#f7f7f7",
                                        }}
                                    >
                                        <Stack direction="row" alignItems="flex-start" spacing={2}>
                                            <Checkbox
                                                checked={formik.values.settings?.checkOnMeEnabled ?? false}
                                                onChange={(e) => {
                                                    console.log(formik.values)
                                                    formik.setFieldValue('settings', {
                                                        ...formik.values.settings ?? {},
                                                        checkOnMeEnabled: e.target.checked,
                                                        defaultContact: false,
                                                        calendarEnabled: false,
                                                    })
                                                }}
                                                sx={{ mt: 0.5 }}
                                            />
                                            <Box>
                                                <Typography fontWeight={600}>Check On Me</Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    Enable 'Check on Me' feature in the mobile app for this team. 
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary" fontWeight={600} mt={1}>
                                                    NOTE:{" "}
                                                    <Typography component="span" variant="body2" color="textSecondary">
                                                        When a 'Check on Me' escalation occurs POM Dispatch will contact the User and dispatch help if they cannot connect with the User.
                                                    </Typography>
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>

                                    <EmergencyOptions
                                        value={formik.values.settings}
                                        setFieldValue={formik.setFieldValue}
                                        handleContactChange={handleContactChange}
                                        nameErrors={nameErrors}
                                        phoneErrors={phoneErrors}
                                        isEdit={isEdit}
                                        teamId={defaultValues?.id ?? undefined}
                                        selectedSchedule={selectedSchedule}
                                        setViewSchedule={setViewSchedule}
                                    />
                                </Box>

                                <Box
                                    textAlign='end'
                                >
                                    <Box
                                        display="flex"
                                        flexDirection="row-reverse"
                                        gap={2}
                                        mt={4}
                                        mb={2}
                                    >
                                        <LoadingButton
                                            variant="contained"
                                            sx={{ ml: 1 }}
                                            size="medium"
                                            onClick={async () => {
                                                handleSubmit();
                                            }}
                                            disabled={submitDisabled || !permissionCheck(customerPermissionFlags.ADMIN)}
                                            loading={teamLoading?.createTeam || teamLoading?.updateTeam}
                                        >
                                            Save
                                        </LoadingButton>

                                        <LoadingButton
                                            variant="outlined"
                                            color="inherit"
                                            size="medium"
                                            onClick={() => {
                                                navigate(-1);
                                            }}
                                            disabled={isSubmitting}
                                            loading={false}
                                        >
                                            Cancel
                                        </LoadingButton>
                                    </Box>
                                    {submitErrors.length > 0 && (
                                        <Box mb={2}>
                                            {submitErrors.map((err, idx) => (
                                                <Typography
                                                    key={idx}
                                                    fontSize={13}
                                                    color="error"
                                                    mb={0.5}
                                                >
                                                    • {err}
                                                </Typography>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            </Box>

                        </Box>
                    </Form>
                </FormikProvider>
            </Box>

            <ScheduleModal
                open={viewSchedule}
                onClose={() => { 
                    setViewSchedule(false);
                    
                    if (!isEdit && selectedTeam) {
                        // Navigate based on source after schedule modal is closed (for newly created teams)
                        if (source === 'group' && groupId) {
                            GroupService.fetchAll(() => {});
                        }
                        setTimeout(() => {
                            navigate(getNavigationPath(selectedTeam));
                        }, 100);
                    } else if (isEdit && selectedTeam) {
                        // Refresh team data when in edit mode to show latest updates
                        TeamService.fetchAll((isValid) => {
                            if (isValid) {
                                // Find and set the updated team data
                                const updatedTeams = store.getState().teams.items;
                                const updatedTeam = updatedTeams.find((t: Team) => t.id === selectedTeam.id);
                                if (updatedTeam) {
                                    TeamService.setTeam(updatedTeam);
                                }
                            }
                        });
                        
                        // Also refresh groups data to keep everything in sync
                        GroupService.fetchAll(() => {});
                    }
                }}
                />

            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={submitError !== null}
                sx={{ mt: 4 }}
                color={'red'}
                onClose={() => {
                    setSubmitError(null);
                }}
                message={submitError}
            />
        </>
    )
}

interface MemberListProps {
    selectedMembers: UserTypes.Model[] | undefined;
    onSelect: (member: UserTypes.Model) => void;
    teams: Team[] | undefined
    setSelectedMembers: (members: UserTypes.Model[]) => void;
}

const MemberSelector: React.FC<MemberListProps> = ({ onSelect, teams, selectedMembers, setSelectedMembers }) => {
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const [selectedSearch, setSelectedSearch] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [user, setUser] = useState<UserTypes.Model | null>(null);

    // Get IDs of already selected members to exclude from search
    const excludeIds = (selectedMembers || []).map(m => m.id);

    // Use paginated search hook instead of loading all users
    const {
        searchInput,
        setSearchInput,
        users: availableMembers,
        totalCount,
        isLoading,
        isFetchingNextPage,
        hasNextPage,
        handleScroll,
    } = useUserSearch({
        excludeIds,
        perPage: 50,
        debounceMs: 300,
    });

    // Filter and sort selected members by selectedSearch (client-side, since it's a small list)
    const filteredSelectedMembers = (selectedMembers || [])
        .filter((m) => `${m.firstName} ${m.lastName}`.toLowerCase().includes(selectedSearch.toLowerCase()))
        .sort((a, b) => {
            const fnCompare = (a.firstName || '').localeCompare(b.firstName || '');
            if (fnCompare !== 0) return fnCompare;
            return (a.lastName || '').localeCompare(b.lastName || '');
        });

    const handleUserClick = (user: any) => {
        setUser(user)
        setDialogOpen(true);
    };

    const confirmSelection = () => {
        if (user) {
            onSelect(user)
        }
        setDialogOpen(false);
    };

    return (
        <Box 
            display="flex"
            flexDirection="column"
            gap={2}
            minWidth='50%'
        >
            <UserSelectConfirmationDialog
                open={dialogOpen}
                userName={user?.firstName || 'this user'}
                userTeam={teams?.find((t) => {
                    return t.id === user?.teamId
                })?.name ?? ''
                }
                onConfirm={confirmSelection}
                onCancel={() => setDialogOpen(false)}
            />
            
            <Box
                display='flex'
                flexDirection='row'
                width='100%'
                gap={1}
                sx={{
                    mt: '1%'
                }}
            >
                {/* Available Members List (Server-side search with pagination) */}
                <Box
                    display='flex'
                    flexDirection='column'
                    gap={1}
                    minWidth='50%'
                >
                    <TextField
                        placeholder="Search members..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        fullWidth
                        InputProps={{
                            endAdornment: (
                                <Box display="flex" alignItems="center" gap={1}>
                                    {totalCount > 0 && (
                                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                            {totalCount.toLocaleString()} found
                                        </Typography>
                                    )}
                                    {(isLoading || isFetchingNextPage) && (
                                        <CircularProgress size={20} />
                                    )}
                                </Box>
                            )
                        }}
                    />
                    <Box
                        height="300px"
                        overflow="auto"
                        border="1px solid #e0e0e0"
                        minWidth='50%'
                        borderRadius={2}
                        p={2}
                        sx={{
                            backgroundColor: '#fafafa',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.05)'
                        }}
                        onScroll={handleScroll}
                    >
                        {isLoading && availableMembers.length === 0 ? (
                            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                <CircularProgress size={32} />
                            </Box>
                        ) : availableMembers.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                {searchInput ? 'No members found' : 'Type to search members'}
                            </Typography>
                        ) : (
                            <>
                                {availableMembers.map((member) => (
                                    <Box
                                        key={member.id}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        p={1.5}
                                        mb={1}
                                        borderRadius={2}
                                        sx={{
                                            backgroundColor: 'white',
                                            transition: 'all 0.2s ease',
                                            cursor: 'pointer',
                                            "&:hover": {
                                                backgroundColor: '#f0f0f0',
                                            },
                                            position: 'relative',
                                        }}
                                        onMouseEnter={() => setHoveredId(member.id)}
                                        onMouseLeave={() => setHoveredId(null)}
                                        onClick={() => {
                                            // Convert to UserTypes.Model format
                                            const userModel: UserTypes.Model = {
                                                id: member.id,
                                                firstName: member.firstName,
                                                lastName: member.lastName,
                                                phoneNumber: member.phoneNumber,
                                                backupEmail: member.backupEmail || null,
                                                teamId: member.teamId || null,
                                                employeeId: member.employeeId || null,
                                                // Required fields with defaults
                                                accountNumber: null,
                                                bloodType: null,
                                                connectionCode: null,
                                                dateOfBirth: null,
                                                devNum: null,
                                                dispatcherComments: null,
                                                emergencyContact: null,
                                                enterprise_id: null,
                                                height: null,
                                                medicalComments: null,
                                                profilePhoto: null,
                                                pushToken: null,
                                                siteNum: null,
                                                weight: null,
                                                appStatus: member.appStatus || null,
                                                appVersion: null,
                                                updatedAt: null,
                                                subscriptionId: '',
                                                teamLeader: null,
                                            };
                                            
                                            if (teams?.find((t) => t.id === member.teamId)) {
                                                handleUserClick(userModel)
                                            } else {
                                                onSelect(userModel)
                                            }
                                        }}
                                    >
                                        <Box display="flex" alignItems="center">
                                            <Avatar
                                                sx={{
                                                    width: 28,
                                                    height: 28,
                                                    fontSize: 14,
                                                    bgcolor: '#e3f2fd',
                                                    color: '#1976d2',
                                                    mr: 2,
                                                }}
                                            >
                                                {member.firstName ? `${member.firstName?.[0] ?? ''}${member.lastName?.[0] ?? ''}` : ''}
                                            </Avatar>
                                            <Box>
                                                <Typography fontSize={13} fontWeight={500}>
                                                    {member.firstName} {member.lastName}
                                                </Typography>
                                                <Typography fontSize={12} color="text.secondary">
                                                    {member.backupEmail}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Box textAlign="right" display="flex" alignItems="center">
                                            <Box mr={1}>
                                                <Typography fontSize={12} color="text.secondary">
                                                    {teams?.find((t) => t.id === member.teamId)?.name ?? ''}
                                                </Typography>
                                                <Typography fontSize={12} color="text.secondary">
                                                    {member.phoneNumber}
                                                </Typography>
                                            </Box>

                                            {hoveredId === member.id && (
                                                <ArrowForwardIosIcon
                                                    sx={{
                                                        fontSize: 14,
                                                        color: 'text.secondary',
                                                        transition: 'opacity 0.2s ease',
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                                
                                {/* Loading indicator for infinite scroll */}
                                {isFetchingNextPage && (
                                    <Box display="flex" justifyContent="center" py={2}>
                                        <CircularProgress size={24} />
                                    </Box>
                                )}
                                
                                {/* End of list indicator */}
                                {!hasNextPage && availableMembers.length > 0 && (
                                    <Typography variant="caption" color="text.secondary" textAlign="center" display="block" py={1}>
                                        End of results
                                    </Typography>
                                )}
                            </>
                        )}
                    </Box>
                </Box>
                
                {/* Selected Members List (client-side search since it's usually small) */}
                <Box display='flex' flexDirection='column' gap={1} minWidth='50%'>
                    <TextField
                        placeholder="Search selected members"
                        value={selectedSearch}
                        onChange={(e) => setSelectedSearch(e.target.value)}
                        fullWidth
                    />
                    <Box
                        height="300px"
                        overflow="auto"
                        border="1px solid #e0e0e0"
                        borderRadius={2}
                        p={2}
                        sx={{ backgroundColor: '#fafafa', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}
                    >
                        {filteredSelectedMembers.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                No selected members
                            </Typography>
                        ) : (
                            filteredSelectedMembers.map((member) => (
                                <Box
                                    key={member.id}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    p={1.5}
                                    mb={1}
                                    borderRadius={2}
                                    sx={{ backgroundColor: 'white', transition: 'all 0.2s ease', cursor: 'pointer', '&:hover': { backgroundColor: '#f0f0f0' }, position: 'relative' }}
                                    onClick={() => {
                                        const updated = (selectedMembers || []).filter((t) => t.id !== member.id);
                                        setSelectedMembers(updated);
                                    }}
                                >
                                    <Box display="flex" alignItems="center">
                                        <Avatar sx={{ width: 28, height: 28, fontSize: 14, bgcolor: '#e3f2fd', color: '#1976d2', mr: 2 }}>
                                            {member.firstName?.[0] ?? ''}{member.lastName?.[0] ?? ''}
                                        </Avatar>
                                        <Box>
                                            <Typography fontSize={13} fontWeight={500}>
                                                {member.firstName} {member.lastName}
                                            </Typography>
                                            <Typography fontSize={12} color="text.secondary">
                                                {member.backupEmail}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Box textAlign="right" display="flex" alignItems="center">
                                        <Box mr={1}>
                                            <Typography fontSize={12} color="text.secondary">
                                                {member.phoneNumber}
                                            </Typography>
                                        </Box>
                                        <DeleteForever sx={{ fontSize: 16, color: '#757575', transition: 'all 0.2s', '&:hover': { color: '#d32f2f' } }} />
                                    </Box>
                                </Box>
                            ))
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};



export default MemberSelector;
